import React, {Key, useRef, useState} from 'react'
import {useApolloClient, useMutation, useQuery} from '@apollo/client'
import {GET_PROVIDER_CONTENT} from '../../utils/api/library'
import DropdownField, {
  BasicDropdownField,
} from '../../components/forms/DropdownField'
import {UPDATE_PUBLICATION_QUERY} from '../../utils/api-utils'
import Spinner from '../../components/icons/Spinner'
import Form from '../../components/forms/utils/Form'
import {Publication} from '../../__generated__/graphql'
import Table from '../../components/lists/Table/Table'
import ActionButton from '../../components/lists/Table/shared/ActionButton'
import {PencilIcon} from '@heroicons/react/24/outline'
import FormModal from '../../components/modals/FormModal'
import _, { isEqual } from 'lodash'

const useDeepMemo = (value) => {
  const ref = useRef([]);

  const pickContentProvider = (publication) => publication.providerContent?.providerContentId
  if (!isEqual(ref.current.map(pickContentProvider), value.map(pickContentProvider))) {
    ref.current = value;
  }

  return ref.current;
}
interface ProviderContentProps {
  publications: (Publication | any)[]
  submitButtonText?: string
  publicationMode?: boolean
  onSubmit?: () => void
}

const PublicationContent = ({loading, contentOptions, index = 0}) => {
  return (
    <DropdownField
      label='Content'
      loading={loading}
      options={contentOptions}
      required
      top
      htmlfor={`publications.${index}.providerContent.providerContentId`}
    />
  )
}
const ProviderContent: React.FC<ProviderContentProps> = ({
  publications,
  publicationMode,
  submitButtonText = 'Save',
  onSubmit,
}) => {
  const {loading, data} = useQuery(GET_PROVIDER_CONTENT)
  const client = useApolloClient()

  const [updatePublication] = useMutation(UPDATE_PUBLICATION_QUERY)
  const [updatingPublications, setUpdatingPublications] = useState(false)
  const [selectedWebsites, setSelectedWebsites] = useState<Key[]>([])
  const [showModal, setShowModal] = useState(false)
  const values = useDeepMemo(publications)

  const handleUpdatePublications = async ({publications}: any) => {
    setUpdatingPublications(true)

    return await Promise.all(
      publications.map(({_id, providerContent: {providerContentId}}) =>
        updatePublication({
          variables: {id: _id, record: {providerContent: {providerContentId}}},
        })
      )
    ).then(
      () => client.refetchQueries({include: ['GetCampaign']})
    ).then(() => {
      setUpdatingPublications(false)
      onSubmit?.()
    })
  }

  if (loading) {
    return <Spinner />
  }

  const contentOptions = data
    ? data.ProviderContentMany.items.map((item) => ({
        value: item._id,
        label: `${item.projectTitle}/${item.title}`,
      }))
    : []

  const onEdit = () => {
    setShowModal(true)
  }
  const formContent = publicationMode ? (
    <PublicationContent loading={loading} contentOptions={contentOptions} />
  ) : (
    <Table
      selectable
      columns={[
        {
          dataIndex: ['website', 'name'],
          title: 'Website',
        },
        {
          dataIndex: ['providerContent', 'providerContentId'],
          title: 'Content',
          render(value, record, index) {
            return (
              <BasicDropdownField
                loading={loading}
                options={contentOptions}
                placeholder='Select Content'
                required
                top
                htmlfor={`publications.${index}.providerContent.providerContentId`}
              />
            )
          },
        },
        {
          key: 'actions',
          title: (
            <div className='px-3'>
              {selectedWebsites.length > 0 && (
                <ActionButton
                  className='mx-3'
                  tooltip={`Edit ${selectedWebsites.length} publications`}
                  row={selectedWebsites}
                  onClick={onEdit}
                  Icon={PencilIcon}
                  padding={false}
                />
              )}
            </div>
          ),
        },
      ]}
      searchable={false}
      data={publications}
      onRowSelectionChange={(selectedRowKeys) =>
        setSelectedWebsites(selectedRowKeys)
      }
      rowKey='_id'
      defaultRowSelection={selectedWebsites}
    />
  )

  return (
    <Form
      loading={updatingPublications}
      values={{publications: values}}
      submitButtonText={submitButtonText}
      onSubmit={handleUpdatePublications}
      className={
        publicationMode
          ? 'flex-1 relative bg-white rounded-md px-4 py-4 mb-10 space-y-6 sm:space-y-5'
          : ''
      }
    >
      {formContent}
      <FormModal
        open={showModal}
        title='Edit websites'
        onOk={({websites, providerContentId}) => handleUpdatePublications({publications: websites.map((_id) => ({_id, providerContent: {providerContentId}}))})}
        onCancel={() => setShowModal(false)}
        defaultValues={{
          websites: selectedWebsites,
          providerContentId: publications.find(({_id}) => _id === selectedWebsites[0])
            ?.providerContent?.providerContentId,
        }}
      >
        <DropdownField
          htmlfor='providerContentId'
          label='Content'
          top
          options={contentOptions}
        />
        <DropdownField
          label='Websites'
          mode='multiple'
          htmlfor='websites'
          options={publications.map((p) => ({
            value: p._id,
            label: p.website.name,
          }))}
        />
      </FormModal>
    </Form>
  )
}

export default ProviderContent
